import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import TaskQueueHeader from "./TaskQueueHeader";
import TaskQueueList from "./TaskQueueList";
import TaskQueueTimeline from "./TaskQueueTimeline";
// import TaskQueueControls, { resolveBlockCurrentTiming } from "./TaskQueueControls";
import {
  buildTaskQueueTimeline,
  filterQueueTasks,
  getKanbanColumns,
  getQueueProgress,
  getTaskStatusLabel,
  groupTasksForKanban,
  normalizeQueueSnapshot,
  resolveCurrentTask,
  sortQueueTasks,
} from "./TaskQueueStore";
import { useStatusPalette } from "../../../utils/statusColors";

const STATUS_DOT = {
  DONE: { bg: "#22c55e", border: "#22c55e", shadow: "none" },
  IN_PROGRESS: { bg: "#3b82f6", border: "#3b82f6", shadow: "0 0 0 3px rgba(59,130,246,0.15)" },
  BLOCKED: { bg: "#f59e0b", border: "#f59e0b", shadow: "none" },
  FAILED: { bg: "#ef4444", border: "#ef4444", shadow: "none" },
  SKIPPED: { bg: "#8b5cf6", border: "#8b5cf6", shadow: "none" },
  CANCELLED: { bg: "#9ca3af", border: "#9ca3af", shadow: "none" },
  PENDING: { bg: "transparent", border: "#d1d5db", shadow: "none" },
};

function formatDetailTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ConnectionBanner({ state }) {
  const _sp = useStatusPalette();
  const n = String(state || "").toLowerCase();
  const cfg =
    n === "reconnecting"
      ? { text: "Live updates reconnecting — queue changes may arrive with a short delay.", ..._sp.amberAlt }
      : n === "polling"
      ? { text: "Live stream unavailable — showing polling-based updates.", ..._sp.blueDark }
      : n === "reconciling"
      ? { text: "Synchronizing queue revisions after missed updates.", ..._sp.blueDark }
      : null;
  if (!cfg) return null;
  return (
    <Box sx={{ px: 2, py: 0.75, borderBottom: `1px solid ${cfg.border}`, backgroundColor: cfg.bg }}>
      <Typography sx={{ fontSize: "11px", color: cfg.color, lineHeight: 1.4 }}>
        {cfg.text}
      </Typography>
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
      {children}
    </Typography>
  );
}

/*
 * TaskDetailActions and supporting helpers (ActionBtn, inputSx, parseLines,
 * parseReplanTasks) are hidden for now — the manual queue-control UI
 * (complete/fail, block/skip, insert/replan) is disabled.
 */

function TaskDetail({ task, queue, acting, onAction }) {
  if (!task) {
    return (
      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
        <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>
          Select a task to inspect details
        </Typography>
      </Box>
    );
  }

  const _sp = useStatusPalette();
  const status = String(task.status || "").toUpperCase();
  const _taskChip = status === "DONE" ? _sp.greenAlt : status === "IN_PROGRESS" ? _sp.blueAlt : status === "FAILED" ? _sp.redAlt : status === "BLOCKED" ? _sp.amberAlt : _sp.zinc;

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary", lineHeight: 1.3, mb: 0.5 }}>
          {task.title || "Untitled task"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
          <Box
            sx={{
              px: 0.75,
              py: "2px",
              borderRadius: "6px",
              backgroundColor: _taskChip.bg,
              border: `1px solid ${_taskChip.border}`,
            }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: _taskChip.color }}>
              {getTaskStatusLabel(status)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            Attempts: {Number(task.attemptCount || 0)}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <SectionLabel>Description</SectionLabel>
          <Typography sx={{ fontSize: "12px", color: "text.secondary", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
            {task.description || "No description provided."}
          </Typography>
        </Box>

        {Array.isArray(task.acceptanceCriteria) && task.acceptanceCriteria.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <SectionLabel>Acceptance Criteria</SectionLabel>
            <Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
              {task.acceptanceCriteria.map((item, i) => (
                <Typography key={`criteria-${i}`} component="li" sx={{ fontSize: "12px", color: "text.secondary", lineHeight: 1.5 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <SectionLabel>Status History</SectionLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
              Created: {formatDetailTimestamp(task.createdAt)}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
              Started: {formatDetailTimestamp(task.startedAt)}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
              Completed: {formatDetailTimestamp(task.completedAt)}
            </Typography>
          </Box>
        </Box>

        {(task.resultSummary || task.failureReason || task.blockedReason || task.skipReason) && (
          <Box sx={{ mb: 2 }}>
            <SectionLabel>Reasons</SectionLabel>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {task.resultSummary && (
                <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>Result: {task.resultSummary}</Typography>
              )}
              {task.failureReason && (
                <Typography sx={{ fontSize: "11px", color: "#dc2626" }}>Failure: {task.failureReason}</Typography>
              )}
              {task.blockedReason && (
                <Typography sx={{ fontSize: "11px", color: "#d97706" }}>Blocked: {task.blockedReason}</Typography>
              )}
              {task.skipReason && (
                <Typography sx={{ fontSize: "11px", color: "#7c3aed" }}>Skipped: {task.skipReason}</Typography>
              )}
            </Box>
          </Box>
        )}

        {Array.isArray(task.dependsOnTaskIds) && task.dependsOnTaskIds.length > 0 && (
          <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
            Depends on: {task.dependsOnTaskIds.join(", ")}
          </Typography>
        )}

        {/* TaskDetailActions (complete/fail, block/skip, insert/replan) hidden for now */}
      </Box>
    </Box>
  );
}

const kanbanPulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.08); }
  50% { box-shadow: 0 0 12px 2px rgba(59,130,246,0.18); }
`;

const kanbanPulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.35); opacity: 0.7; }
`;

function KanbanCard({ task, selected, onClick, index }) {
  const _ksp = useStatusPalette();
  const status = String(task?.status || "").toUpperCase();
  const dot = STATUS_DOT[status] || STATUS_DOT.PENDING;
  const isActive = status === "IN_PROGRESS";

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: isActive ? _ksp.blue.border : selected ? _ksp.blue.border : "divider",
        backgroundColor: isActive ? _ksp.blue.bg : selected ? _ksp.blue.bg : "background.paper",
        cursor: "pointer",
        transition: "all 150ms ease",
        boxShadow: selected ? "0 0 0 1px rgba(59,130,246,0.12)" : "0 1px 2px rgba(0,0,0,0.04)",
        ...(isActive && {
          animation: `${kanbanPulseGlow} 2.4s ease-in-out infinite`,
        }),
        "&:hover": {
          borderColor: selected || isActive ? _ksp.blue.border : "divider",
          backgroundColor: selected || isActive ? _ksp.blue.bg : "action.hover",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            border: `2px solid ${dot.border}`,
            backgroundColor: dot.bg,
            boxShadow: dot.shadow,
            flexShrink: 0,
            ...(isActive && {
              animation: `${kanbanPulseDot} 2.4s ease-in-out infinite`,
            }),
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "text.primary", lineHeight: 1.3, whiteSpace: "normal", wordBreak: "break-word" }}>
            {typeof index === "number" ? `${index + 1}. ` : ""}{task?.title || "Untitled task"}
          </Typography>
          <Typography sx={{ fontSize: "10px", color: "text.secondary", mt: 0.25 }}>
            {getTaskStatusLabel(status)} · Attempt {Number(task?.attemptCount || 0)}
          </Typography>
        </Box>
      </Box>
      {task?.updatedAt && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5, pl: "22px" }}>
          <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>
            {new Date(task.updatedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function resolveKanbanDropAction(task, sourceColumnKey, destColumnKey, queue) {
  if (sourceColumnKey === destColumnKey) return null;
  const status = String(task?.status || "").toUpperCase();
  const hasInProgress = (queue?.tasks || []).some(
    (t) => String(t?.status || "").toUpperCase() === "IN_PROGRESS" && t.taskId !== task.taskId
  );

  if (destColumnKey === "in_progress") {
    if (status === "PENDING" && !hasInProgress) {
      return { action: "start_next", idempotencyKey: `kanban-start-${task.taskId}-${Date.now()}` };
    }
    if ((status === "FAILED" || status === "BLOCKED" || status === "DONE" || status === "SKIPPED") && !hasInProgress) {
      return { action: "retry_task", taskId: task.taskId, idempotencyKey: `kanban-retry-start-${task.taskId}-${Date.now()}` };
    }
    return null;
  }

  if (destColumnKey === "done") {
    if (status === "IN_PROGRESS") {
      return { action: "complete_current", resultSummary: "Manually completed via kanban", idempotencyKey: `kanban-complete-${task.taskId}-${Date.now()}` };
    }
    if (status === "PENDING") {
      return { action: "skip_task", taskId: task.taskId, skipReason: "Skipped via kanban", idempotencyKey: `kanban-skip-${task.taskId}-${Date.now()}` };
    }
    return null;
  }

  if (destColumnKey === "todo") {
    if (status === "FAILED" || status === "BLOCKED" || status === "CANCELLED"
        || status === "DONE" || status === "SKIPPED") {
      return { action: "retry_task", taskId: task.taskId, idempotencyKey: `kanban-retry-${task.taskId}-${Date.now()}` };
    }
    return null;
  }

  if (destColumnKey === "blocked_failed_cancelled") {
    if (status === "IN_PROGRESS") {
      return { action: "block_current", blockedReason: "Blocked via kanban", idempotencyKey: `kanban-block-${task.taskId}-${Date.now()}` };
    }
    return null;
  }

  return null;
}

function KanbanBoard({ groupedColumns, selectedTaskId, onSelectTask, allTasks, onAction, acting, queue }) {
  const _ksp = useStatusPalette();
  return (
    <Box sx={{ display: "flex", gap: 1.25, minWidth: "max-content", alignItems: "flex-start" }}>
      {groupedColumns.map((column) => (
        <Droppable droppableId={column.key} key={column.key}>
          {(provided, dropSnapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                width: 240,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: dropSnapshot.isDraggingOver ? _ksp.blue.border : "divider",
                backgroundColor: dropSnapshot.isDraggingOver ? _ksp.blue.bg : "action.hover",
                overflow: "hidden",
                transition: "border-color 150ms ease, background-color 150ms ease",
              }}
            >
              <Box
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderBottom: 1, borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.primary" }}>
                  {column.label}
                </Typography>
                <Typography sx={{ fontSize: "10px", color: "text.disabled", fontWeight: 600 }}>
                  {column.rows.length}
                </Typography>
              </Box>
              <Box sx={{ p: 0.75, display: "flex", flexDirection: "column", gap: 0.75, minHeight: 60 }}>
                {column.rows.length === 0 && !dropSnapshot.isDraggingOver ? (
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      border: 1, borderStyle: "dashed", borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>
                      No tasks
                    </Typography>
                  </Box>
                ) : (
                  column.rows.map((task, idx) => {
                    const globalIndex = (allTasks || []).findIndex((t) => t.taskId === task.taskId);
                    return (
                      <Draggable
                        key={task.taskId}
                        draggableId={task.taskId}
                        index={idx}
                        isDragDisabled={acting}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <Box
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            sx={{
                              opacity: dragSnapshot.isDragging ? 0.85 : 1,
                              transform: dragSnapshot.isDragging ? "rotate(2deg)" : "none",
                              transition: dragSnapshot.isDragging ? "none" : "opacity 150ms ease",
                            }}
                          >
                            <KanbanCard
                              task={task}
                              index={globalIndex >= 0 ? globalIndex : undefined}
                              selected={selectedTaskId === task.taskId}
                              onClick={() => onSelectTask(task)}
                            />
                          </Box>
                        )}
                      </Draggable>
                    );
                  })
                )}
                {provided.placeholder}
              </Box>
            </Box>
          )}
        </Droppable>
      ))}
    </Box>
  );
}

function QueueSelector({ history, selectedQueueId, onSelect }) {
  if (!Array.isArray(history) || history.length <= 1) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        px: 1.5,
        py: 0.75,
        borderBottom: 1,
        borderColor: "divider",
        overflowX: "auto",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { height: 4 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "action.disabled",
          borderRadius: 2,
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      }}
    >
      {history.map((snap, idx) => {
        const qId = snap?.queue?.queueId;
        const isSelected = qId === selectedQueueId;
        const status = String(snap?.queue?.status || "").toUpperCase();
        const statusColor =
          status === "ACTIVE"
            ? "#3b82f6"
            : status === "COMPLETED"
            ? "#22c55e"
            : status === "CANCELLED"
            ? "#9ca3af"
            : status === "PAUSED"
            ? "#f59e0b"
            : "#6b7280";
        const goal = String(snap?.queue?.goal || "").trim();
        const truncatedGoal = goal.length > 28 ? goal.slice(0, 26) + "\u2026" : goal;
        const label = truncatedGoal || `Queue ${history.length - idx}`;
        return (
          <Box
            key={qId || idx}
            onClick={() => onSelect?.(qId)}
            title={goal || undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: isSelected ? "action.selected" : "transparent",
              border: isSelected ? "1px solid" : "1px solid transparent",
              borderColor: isSelected ? "divider" : "transparent",
              "&:hover": { backgroundColor: "action.hover" },
              flexShrink: 0,
              maxWidth: 200,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: statusColor,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? "text.primary" : "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function TaskQueuePanel({
  snapshot,
  loading,
  applying,
  error,
  connectionState,
  queueHistory,
  selectedQueueId,
  onSelectQueue,
  onRefresh,
  onAction,
  onClose,
  onExpandedModeChange,
}) {
  const normalized = normalizeQueueSnapshot(snapshot);
  const queue = normalized.queue;
  const queueErrorMessage =
    typeof normalized.error?.message === "string" ? normalized.error.message : "";
  const effectiveError = error || queueErrorMessage;

  const [filterKey, setFilterKey] = useState("all");
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [expandedMode, setExpandedMode] = useState(false);

  const rawTasks = useMemo(() => queue?.tasks || [], [queue?.tasks]);
  const sortedTasks = useMemo(() => sortQueueTasks(rawTasks), [rawTasks]);
  const filteredTasks = useMemo(
    () => filterQueueTasks(sortedTasks, filterKey),
    [filterKey, sortedTasks]
  );
  const groupedKanbanColumns = useMemo(
    () => groupTasksForKanban(sortedTasks),
    [sortedTasks]
  );
  const kanbanColumns = useMemo(() => getKanbanColumns(), []);
  const progress = useMemo(() => getQueueProgress(queue), [queue]);
  const timelineItems = useMemo(() => buildTaskQueueTimeline(queue), [queue]);

  useEffect(() => {
    if (!queue) { setSelectedTaskId(""); return; }
    const currentTask = resolveCurrentTask(queue);
    if (currentTask?.taskId) {
      setSelectedTaskId((c) => c || currentTask.taskId);
      return;
    }
    if (sortedTasks.length > 0) {
      setSelectedTaskId((c) => c || sortedTasks[0].taskId);
    } else {
      setSelectedTaskId("");
    }
  }, [queue, sortedTasks]);

  useEffect(() => {
    if (!selectedTaskId) return;
    const stillExists = sortedTasks.some((t) => t.taskId === selectedTaskId);
    if (!stillExists) setSelectedTaskId(sortedTasks[0]?.taskId || "");
  }, [selectedTaskId, sortedTasks]);

  const selectedTask =
    sortedTasks.find((t) => t.taskId === selectedTaskId) || resolveCurrentTask(queue);

  useEffect(() => {
    if (typeof onExpandedModeChange !== "function") return;
    onExpandedModeChange(expandedMode);
  }, [expandedMode, onExpandedModeChange]);

  const handleKanbanDragEnd = useCallback(
    (result) => {
      const { source, destination, draggableId } = result;
      if (!destination || !onAction) return;
      if (source.droppableId === destination.droppableId) return;

      const task = sortedTasks.find((t) => t.taskId === draggableId);
      if (!task) return;

      const actionPayload = resolveKanbanDropAction(
        task,
        source.droppableId,
        destination.droppableId,
        queue
      );
      if (!actionPayload) return;

      onAction(actionPayload);
    },
    [sortedTasks, queue, onAction]
  );

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <TaskQueueHeader
        queue={queue}
        progress={progress}
        loading={loading}
        connectionState={connectionState}
        expanded={expandedMode}
      />
      <QueueSelector
        history={queueHistory}
        selectedQueueId={selectedQueueId}
        onSelect={onSelectQueue}
      />
      <ConnectionBanner state={connectionState} />

      {!queue ? (
        <Box sx={{ px: 2, py: 3, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 0.5, textAlign: "center" }}>
            No queue is active for this conversation yet.
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "text.disabled", textAlign: "center" }}>
            Ask the agent to run a planning step, then refresh this panel.
          </Typography>
          {effectiveError && (
            <Typography sx={{ mt: 1.5, fontSize: "11px", color: "#dc2626" }}>
              {effectiveError}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {/* TaskQueueControls (pause/resume/recover/cancel) hidden for now */}

          {!expandedMode ? (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  borderTop: 1, borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    borderRight: 1, borderColor: "divider",
                    overflow: "hidden",
                  }}
                >
                  <TaskQueueList
                    loading={loading}
                    tasks={filteredTasks}
                    allTasks={rawTasks}
                    selectedTaskId={selectedTask?.taskId || ""}
                    activeFilter={filterKey}
                    onFilterChange={setFilterKey}
                    onSelectTask={(task) => setSelectedTaskId(task?.taskId || "")}
                  />
                </Box>
                <Box
                  sx={{
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <TaskDetail task={selectedTask} queue={queue} acting={Boolean(applying || loading)} onAction={onAction} />
                </Box>
              </Box>
              <Box sx={{ borderTop: 1, borderColor: "divider" }}>
                <TaskQueueTimeline events={timelineItems} />
              </Box>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 280px)",
                  borderTop: 1, borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    minHeight: 0,
                    overflowX: "auto",
                    overflowY: "auto",
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRight: 1, borderColor: "divider",
                  }}
                >
                  <DragDropContext onDragEnd={handleKanbanDragEnd}>
                    <KanbanBoard
                      groupedColumns={groupedKanbanColumns.map((col) => ({
                        ...col,
                        label:
                          kanbanColumns.find((k) => k.key === col.key)?.label || col.label,
                      }))}
                      selectedTaskId={selectedTask?.taskId || ""}
                      onSelectTask={(task) => setSelectedTaskId(task?.taskId || "")}
                      allTasks={rawTasks}
                      onAction={onAction}
                      acting={Boolean(applying || loading)}
                      queue={queue}
                    />
                  </DragDropContext>
                </Box>
                <Box
                  sx={{
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <TaskDetail task={selectedTask} queue={queue} acting={Boolean(applying || loading)} onAction={onAction} />
                  <Box sx={{ borderTop: 1, borderColor: "divider" }}>
                    <TaskQueueTimeline events={timelineItems} />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
